import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiShoppingBag, FiMenu, FiX, FiUser, FiLogOut, FiUser as FiProfile } from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import CartDrawer from './CartDrawer';
import AuthModal from './AuthModal';
import './Navbar.css';

const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/shop', label: 'Shop' },
    { to: '/about', label: 'Our Story' },
    { to: '/contact', label: 'Contact' },
];

const Navbar = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    const [authOpen, setAuthOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const { totalItems } = useCart();
    // FIX: AuthContext's `user` gets populated from whichever token exists —
    // admin OR customer — because logging into /admin also sets a shared
    // `user` session used app-wide (needed so the admin panel survives a
    // page refresh). This Navbar only ever renders on the public storefront,
    // and was showing that name to real visitors any time the site owner
    // happened to be logged into /admin in the same browser. Only a
    // customer-role session should ever be reflected here — an admin
    // session is treated as "not logged in" from the storefront's
    // perspective, exactly as a real visitor would see it.
    const { user: authUser, signOut } = useAuth();
    const user = authUser?.role === 'customer' ? authUser : null;

    const location = useLocation();
    const navigate = useNavigate();
    const userMenuRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
        setUserMenuOpen(false);
    }, [location]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!menuOpen) return;
        const handleOutsideClick = (e) => {
            if (!e.target.closest('.navbar')) setMenuOpen(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [menuOpen]);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    const handleUserClick = () => {
        if (user) {
            setUserMenuOpen(prev => !prev);
        } else {
            setAuthOpen(true);
        }
    };

    const handleLogout = () => {
        // Only signs the customer out — does not touch the admin session,
        // since this button only ever appears for a customer-role user now.
        signOut();
        setUserMenuOpen(false);
        setMenuOpen(false);
        navigate('/');
    };

    return (
        <>
            <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
                <div className="navbar-inner">

                    {/* ── HAMBURGER (mobile only) ── */}
                    <button
                        className="nav-hamburger"
                        onClick={() => setMenuOpen(prev => !prev)}
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={menuOpen}
                    >
                        {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
                    </button>

                    {/* ── LOGO ── */}
                    <Link to="/" className="navbar-logo">
                        <img
                            src="/navbar-logo.png"
                            alt="Raajdhani by Sonia"
                            className="logo-img"
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    </Link>

                    {/* ── DESKTOP NAV LINKS ── */}
                    <div className="nav-links">
                        {navLinks.map(link => (
                            <Link
                                key={link.to}
                                to={link.to}
                                className={`nav-link ${location.pathname === link.to ? 'active' : ''}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    {/* ── ACTION BUTTONS ── */}
                    <div className="nav-actions">

                        {/* User / Login */}
                        <div className="user-menu-wrapper" ref={userMenuRef}>
                            <button
                                className="nav-icon-btn"
                                onClick={handleUserClick}
                                title={user ? 'Account' : 'Login'}
                                aria-label={user ? 'Account menu' : 'Login'}
                            >
                                <FiUser size={20} />
                                {user && (
                                    <span className="user-name">
                                        {user.name?.split(' ')[0]}
                                    </span>
                                )}
                            </button>

                            <AnimatePresence>
                                {userMenuOpen && user && (
                                    <motion.div
                                        className="user-dropdown"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <Link to="/profile" className="dropdown-item">
                                            <FiProfile size={16} /> Profile
                                        </Link>
                                        <button
                                            onClick={handleLogout}
                                            className="dropdown-item"
                                        >
                                            <FiLogOut size={16} /> Logout
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Cart */}
                        <button
                            className="nav-icon-btn"
                            onClick={() => setCartOpen(true)}
                            aria-label={`Open cart, ${totalItems} items`}
                        >
                            <FiShoppingBag size={20} />
                            {totalItems > 0 && (
                                <span className="cart-badge">{totalItems}</span>
                            )}
                        </button>
                    </div>
                </div>

                {/* ── MOBILE MENU ── */}
                <AnimatePresence>
                    {menuOpen && (
                        <motion.div
                            className="mobile-menu"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25, ease: 'easeInOut' }}
                        >
                            {navLinks.map(link => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className={`mobile-nav-link ${location.pathname === link.to ? 'active' : ''}`}
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {link.label}
                                </Link>
                            ))}

                            {user ? (
                                <>
                                    <Link
                                        to="/profile"
                                        className="mobile-nav-link"
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        Profile
                                    </Link>
                                    <button
                                        onClick={handleLogout}
                                        className="logout-btn"
                                    >
                                        Logout
                                    </button>
                                </>
                            ) : (
                                <button
                                    className="logout-btn"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        setAuthOpen(true);
                                    }}
                                >
                                    Login / Sign Up
                                </button>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </nav>

            <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
            <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
        </>
    );
};

export default Navbar;